import type { ReactNode } from 'react';
import React, { createContext, useState, useContext } from 'react';
import { Modal } from '@bluevision/components';
import { useTranslation } from 'react-i18next';

import { queryCurrent } from '@/services/authority/login';
import { getVaGrayscale } from '@/services/offlinepayment';
import { IRealNameIdentification } from '@/services/identification/interface';

import { Identification } from './index';
import { IEnterpriseCertification } from '@/services/enterpriseCertification/interface';
import type { CurrentUserInfo } from '@/typings/user';
import { handleIngCertify, handleUnCertified } from './data';

const validateUser = async (): Promise<
  {
    identificationValid: boolean;
    claimValid: boolean;
  } & Partial<CurrentUserInfo>
> => {
  const [res] = await queryCurrent();
  return {
    // 实名认证状态是否为已认证
    identificationValid:
      res?.verifyInfo?.verifyStatus ===
      IRealNameIdentification.Common.IdentificationStatus.VERIFIED,
    // 认领状态是否为已认领
    claimValid: res?.claimStatus === IRealNameIdentification.Common.ClaimStatus.CLAIMED,

    ...res,
  };
};

interface ModalShowConfig {
  /** 是否跳过实名认证检查，直接打开实名认证表单 */
  skipIdentificationCheck?: boolean;
  /** 是否需要检查认领状态 */
  needCheckClaim?: boolean;

  /** 是否需要进行企业认证 目前只有媒体账户充值需要 */
  needEnterpriseCertification?: boolean;
}

type OpenModalFunc = (callback: () => void, config?: ModalShowConfig) => void;

interface ModalContextProps {
  openModal: OpenModalFunc;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

/**
 * 这里暴露 loading 无用，loading 被多个地方共用，一处设置 setLoading(true) 后，所有用到它的组件或地方都会一起变化。
 * 若要每个使用场景的 loading 互不影响，需要各自管理各自的 loading 状态。
 */
export const IdentificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();

  // 预付实名认证表单(输身份证号)
  const [isVisible, setIsVisible] = useState(false);
  const [onSuccessCallback, setOnSuccessCallback] = useState<() => void>(() => () => {});
  // 预付实名认证未完成提示(去认证 -> 打开表单)
  const [modalVisible, setModalVisible] = useState(false); // 新增 state

  const openModal = async (callback: () => void, config?: ModalShowConfig) => {
    // 实名认证灰度能力获取
    const [grayRes] = await getVaGrayscale();

    // 需要验证是否需要认证，来决定是否需要打开实名认证弹窗
    const validInfo = await validateUser();

    // 是否需要提示认领状态不正确
    const showClaimWarning = config?.needCheckClaim && !validInfo.claimValid;

    // P2: 处理认领状态逻辑
    const handleClaimFunc = () => {
      // 如果已经实名认证，则验证认领状态
      if (showClaimWarning) {
        Modal.open({
          type: 'warning',
          title: t('current_user_information_is_incomplete'),
          children: t('please_contact_the_sales_or_account_manager_for_confirmation'),
          okText: t('got_it'),
          centered: true,
        });
        return;
      } else {
        // 否则直接进行后续操作
        callback();
        return;
      }
    };

    // P1: 处理预付灰度逻辑
    const handlePrepayFunc = () => {
      // 直接打开实名认证弹窗 (仅右上角个人信息处使用，所以不考虑 claim 状态检测)
      if (config?.skipIdentificationCheck) {
        setOnSuccessCallback(() => callback);
        setIsVisible(true);
        return;
      }

      // 如果没有实名认证，则提示用户进行实名认证
      if (!validInfo.identificationValid) {
        setModalVisible(true);
      } else {
        // 如果已经实名认证，则验证认领状态 P2
        handleClaimFunc();
      }
    };

    // 处理后付灰度逻辑
    const handlePostpaidFunc = () => {
      // 需要企业认证
      if (config?.needEnterpriseCertification && validInfo?.signCertificationRequired) {
        // 已认证 判断认领状态 P2
        if (
          validInfo?.signCertificationStatus ===
          IEnterpriseCertification.Common.EnterpriseCertificationStatus.CERTIFIED
        ) {
          handleClaimFunc();
        }
        // 未认证 有正在进行中的认证
        else if (
          validInfo?.signCertificationStatus ===
            IEnterpriseCertification.Common.EnterpriseCertificationStatus.ING &&
          validInfo?.pendingCertificationUrl
        ) {
          handleIngCertify({
            certificationType: validInfo?.defaultCertificationType,
            pendingCertificationUrl: validInfo?.pendingCertificationUrl,
          });
          return;
        }
        // 未认证 无正在进行中的认证
        else {
          handleUnCertified({ certificationType: validInfo?.defaultCertificationType });
          return;
        }
      } else {
        // 不需要企业认证
        handleClaimFunc();
      }
    };

    // 流程: https://bluefocus.feishu.cn/wiki/YGbPwTFsniTz9fkqigAclPW7nFe#share-Rq0jdjMJuoBNnnxURSKcss3Dndf
    // 1. 后付灰度❌ 预付灰度❌ -> P2 判断认领状态
    if (!validInfo?.signCertificationRequired && !grayRes?.real_name_grayscale) {
      handleClaimFunc();
    }
    // 2. 后付灰度❌ 预付灰度✅ -> P1
    else if (!validInfo?.signCertificationRequired && grayRes?.real_name_grayscale) {
      handlePrepayFunc();
    }
    // 3. 后付灰度✅ 预付灰度✅/❌
    else if (validInfo?.signCertificationRequired) {
      handlePostpaidFunc();
    }
  };

  const closeModal = () => {
    setIsVisible(false);
  };

  return (
    <ModalContext.Provider value={{ openModal }}>
      {children}
      <Identification
        visible={isVisible}
        onCancel={closeModal}
        onIdentifySuccess={onSuccessCallback}
      />
      <Modal
        open={modalVisible}
        title={t('real_name_authentication_incomplete')}
        onCancel={() => setModalVisible(false)}
        okText={t('go_to_identification')}
        cancelButtonProps={{ size: 'large' }}
        cancelText={t('cancel')}
        okButtonProps={{ size: 'large' }}
        onOk={() => {
          setIsVisible(true);
          setModalVisible(false);
        }}
      >
        {t('complete_real_name_authentication')}
      </Modal>
    </ModalContext.Provider>
  );
};

export const useIdentification = (): ModalContextProps => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useIdentification must be used within a IdentificationProvider');
  }
  return context;
};
